/**
 * 区間ごとの力モデル（docs/physics/physics-model.md 4 章）。
 * サンプル軌道の `segment`（区間番号）で引く。通常・ブロック区間は特別な力を持たない。
 */
export type SegmentSpec =
  | { readonly type: 'normal' | 'block' }
  | {
      /** リフト（巻き上げ）：速度が `liftSpeed` 未満のあいだ、速度を `liftSpeed` に拘束する */
      readonly type: 'lift';
      /** 巻き上げ速度 [m/s] */
      readonly liftSpeed: number;
    }
  | {
      /** ブレーキ：速度が `targetSpeed` を超えるあいだ、最大減速度で減速する */
      readonly type: 'brake';
      /** 最大減速度 [m/s²] */
      readonly maxDeceleration: number;
      /** 目標速度 [m/s]。0 は停止ブレーキ（停止後は静止を保持する） */
      readonly targetSpeed: number;
    }
  | {
      /**
       * 発進加速（ローンチ）：速度が `targetSpeed` 未満のあいだ、推進の加速度 `maxAcceleration` を加える
       * （推進力 `F_p = m·a_p`。重力・抵抗はそのまま働く）。目標速度に達したら推進を止める
       */
      readonly type: 'launch';
      /** 目標速度 [m/s] */
      readonly targetSpeed: number;
      /** 推進の最大加速度 `a_p` [m/s²] */
      readonly maxAcceleration: number;
    }
  | {
      /** 駅：速度が `dispatchSpeed` を超えれば制動し、下回れば `dispatchSpeed` で送り出す */
      readonly type: 'station';
      /** 発車時の送り出し速度 [m/s] */
      readonly dispatchSpeed: number;
      /** 制動の最大減速度 [m/s²]（既定 6） */
      readonly maxDeceleration?: number;
    };

/** 駅の既定の制動の減速度 [m/s²]。 */
export const DEFAULT_STATION_DECELERATION = 6;

/** 区間が、速度を下限に拘束して前へ送る装置（リフト・駅の送り出し）を持つなら、その速度。 */
export function conveyorSpeed(spec: SegmentSpec | undefined): number | undefined {
  if (spec?.type === 'lift') return spec.liftSpeed;
  if (spec?.type === 'station') return spec.dispatchSpeed;
  return undefined;
}

/** 区間の制動：目標速度と最大減速度。制動のない区間は `undefined`。 */
export function brakingOf(
  spec: SegmentSpec | undefined,
): { readonly targetSpeed: number; readonly maxDeceleration: number } | undefined {
  if (spec?.type === 'brake') {
    return { targetSpeed: spec.targetSpeed, maxDeceleration: spec.maxDeceleration };
  }
  if (spec?.type === 'station') {
    return {
      targetSpeed: spec.dispatchSpeed,
      maxDeceleration: spec.maxDeceleration ?? DEFAULT_STATION_DECELERATION,
    };
  }
  return undefined;
}

/** 区間の推進：目標速度と最大加速度。推進のない区間は `undefined`。 */
export function propulsionOf(
  spec: SegmentSpec | undefined,
): { readonly targetSpeed: number; readonly maxAcceleration: number } | undefined {
  return spec?.type === 'launch'
    ? { targetSpeed: spec.targetSpeed, maxAcceleration: spec.maxAcceleration }
    : undefined;
}

/**
 * 停止ブレーキ（目標速度 0）の区間か。停止後は静止を保持する。
 * 停止ブレーキ・リフト・駅では、速度が 0 以下になっても失速（stall）とはみなさない（物理仕様 5 章）。
 */
export function isStopping(spec: SegmentSpec | undefined): boolean {
  return spec?.type === 'brake' && spec.targetSpeed === 0;
}

/** 失速イベントを出さない区間（リフト・駅・停止ブレーキ・発進加速）か。 */
export function exemptFromStall(spec: SegmentSpec | undefined): boolean {
  return (
    spec?.type === 'lift' || spec?.type === 'station' || spec?.type === 'launch' || isStopping(spec)
  );
}
